/**
 * "Restart hub" in Settings > General (ADR 0088): for admins, when this hub can restart itself.
 * The confirmation says what ends (sessions on the hub's own computer) and what stays (every
 * other machine reconnects on its own); it waits for the new hub before it closes.
 */
import { RotateCw } from '@orgus/ui/icons';
import { useEffect, useState } from 'react';
import { roleAtLeast } from '@orgus/protocol';
import type { HubLifecycleState } from '@orgus/protocol';
import { Button, ConfirmDialog, toast } from '@orgus/ui';
import { api } from '../../lib/api.ts';
import { useConnection } from '../../lib/store.ts';
import { useMe } from '../auth/useMe.ts';
import { consequence, restartAndWait, startedBy } from './restart.ts';

export function RestartHub({ onRestarted }: { onRestarted: () => void }) {
  const { mode } = useConnection();
  const role = useMe((s) => s.me?.actor.role);
  const allowed = mode !== 'mock' && !!role && roleAtLeast(role, 'admin');
  const [state, setState] = useState<HubLifecycleState | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!allowed) return;
    let stop = false;
    api<HubLifecycleState>('/api/hub/lifecycle').then(
      (s) => !stop && setState(s),
      () => undefined,
    );
    return () => {
      stop = true;
    };
  }, [allowed, open]);

  if (!allowed || !state?.canRestart) return null;
  const ends = state.endsOnRestart > 0;
  return (
    <>
      <Button size="sm" variant="secondary" icon={<RotateCw />} onClick={() => setOpen(true)}>
        Restart hub
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title="Restart this hub?"
        description={consequence(state)}
        confirmLabel="Restart hub"
        cancelLabel="Keep running"
        tone={ends ? 'danger' : 'primary'}
        onConfirm={async () => {
          await restartAndWait();
          onRestarted();
          toast('Hub restarted');
        }}
      >
        <p className="text-caption text-fg-subtle">{startedBy(state)}</p>
      </ConfirmDialog>
    </>
  );
}
